import { useCallback } from "react"
import { useTranslation } from "react-i18next"
import { ListChecks, SquarePen, type LucideIcon } from "lucide-react"
import { useLocation, useNavigate } from "react-router"

import { Tabs, TabsList, TabsTrigger } from "@f2e/ui/components/base"
import { cn } from "@f2e/ui/lib/utils"

type PageType = "playground" | "evaluation"

export interface PageBreadcrumbSelectorProps {
  workspaceId: string
  agentId: string
  currentPage: PageType
  className?: string
}

const PageBreadcrumbSelector = ({
  workspaceId,
  agentId,
  currentPage,
  className,
}: PageBreadcrumbSelectorProps) => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()

  const handlePageChange = useCallback(
    (targetPage: string) => {
      if (targetPage === currentPage) return

      const searchParams = new URLSearchParams(location.search)
      if (targetPage === "playground") {
        searchParams.delete("tab")
      } else if (targetPage === "evaluation") {
        searchParams.set("tab", "evaluation")
      }

      const search = searchParams.toString()
      // Commit before Radix's focus event can repeat its mousedown navigation.
      navigate(
        {
          pathname: `/workspaces/${workspaceId}/agents/${agentId}`,
          search: search ? `?${search}` : "",
        },
        { flushSync: true }
      )
    },
    [agentId, currentPage, location.search, navigate, workspaceId]
  )

  const pages: Array<{
    value: PageType
    label: string
    Icon: LucideIcon
  }> = [
    {
      value: "playground",
      label: t("workspace.pageBreadcrumb.playground"),
      Icon: SquarePen,
    },
    {
      value: "evaluation",
      label: t("workspace.pageBreadcrumb.evaluationTable"),
      Icon: ListChecks,
    },
  ]

  return (
    <Tabs
      value={currentPage}
      onValueChange={handlePageChange}
      className={cn("w-full", className)}
    >
      <TabsList
        aria-label="Agent page sections"
        className="h-11 w-full justify-start gap-6 rounded-none border-b border-border bg-transparent p-0"
      >
        {pages.map(({ value, label, Icon }) => (
          <TabsTrigger
            key={value}
            value={value}
            aria-label={`Navigate to ${label}`}
            className="h-11 flex-none rounded-none border-0 border-b-2 border-transparent bg-transparent px-0 pb-3 pt-2 text-base font-medium text-muted-foreground shadow-none transition-colors hover:text-foreground data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none"
          >
            <Icon className="size-4 stroke-[2.25]" />
            <span>{label}</span>
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  )
}

PageBreadcrumbSelector.displayName = "PageBreadcrumbSelector"

export default PageBreadcrumbSelector
